import { useGridFilter } from 'ag-grid-react';
import type { IDoesFilterPassParams } from 'ag-grid-community';
import type { ColDef } from 'ag-grid-community';
import { Button, TextField } from '@seeqdev/qomponents';
import ms from 'ms';

import { useCallback, useEffect, useState } from 'react';
import { nodeDurationMillis } from './utilities';
import { useTranslation } from 'react-i18next';

const comparisonOperators = ['>', '<', '='] as const;
type Operator = typeof comparisonOperators[number];

interface DurationModel {
  textFieldValue: string | undefined;
  operator: Operator;
  nanoSeconds: number;
}

const isValidFilter = (userInput: string, parsedValue: number): boolean => {
  const containsNonNumeric = /[a-zA-Z]/.test(userInput);
  const validConversion = !isNaN(parsedValue) && parsedValue !== 0;
  return validConversion && containsNonNumeric;
};

const customFilterChangeEvent = {
  type: 'filterChanged',
  source: 'customFilter',
  columns: [],
};

interface DurationFilterProps {
  model: DurationModel;
  onModelChange: (params: { nanoSeconds: number; operator: Operator | ''; textFieldValue: string }) => void;
  colDef: ColDef;
  api: { dispatchEvent: (params: typeof customFilterChangeEvent) => void };
}

export const DurationFilter: React.FunctionComponent<DurationFilterProps> = ({ model, onModelChange, colDef, api }) => {
  const { t } = useTranslation();
  const [textFieldValue, setTextFieldValue] = useState(model?.textFieldValue || '');
  const [durationIsValid, setDurationIsValid] = useState(true);
  const [comparisonType, setComparisonType] = useState(model?.operator || '>');

  useEffect(() => {
    try {
      const nanoSeconds = ms(textFieldValue as ms.StringValue) * 1e6;
      const isValid = isValidFilter(textFieldValue, nanoSeconds);
      setDurationIsValid(isValid);
      if (isValid) {
        onModelChange({ nanoSeconds, operator: comparisonType, textFieldValue });
        api.dispatchEvent(customFilterChangeEvent);
      }
    } catch {
      setDurationIsValid(false);
    }
  }, [textFieldValue, comparisonType, onModelChange]);

  const doesFilterPass = useCallback(
    ({ node }: IDoesFilterPassParams<any>): boolean => {
      if (!model) {
        return true;
      }
      let value;
      try {
        value = (nodeDurationMillis(node) ?? 0) * 1e6;

        switch (model.operator) {
          case '>':
            return value > model.nanoSeconds;
          case '<':
            return value < model.nanoSeconds;
          case '=':
            return value === model.nanoSeconds;
          default:
            return true;
        }
      } catch (err) {
        console.error(`Error calculating the duration for node. Error message : ${err}`, node);
        return true;
      }
    },
    [model, colDef.field],
  );

  const afterGuiAttached = () => {
    setComparisonType(model?.operator || '>');
    setTextFieldValue(model?.textFieldValue || '');
  };

  useGridFilter({
    doesFilterPass,
    afterGuiAttached,
    getModelAsString: () => `${model?.operator} ${model?.textFieldValue}`,
  });

  function feedbackMessage() {
    if (durationIsValid) {
      return `Filtered to duration ${comparisonType} ${textFieldValue}.`;
    } else if (textFieldValue.length > 0) {
      return `Invalid duration (${textFieldValue}).`;
    } else {
      return '';
    }
  }

  return (
    <div className="tw:flex tw:flex-col">
      <div className="tw:flex tw:flex-row tw:m-2 tw:gap-4">
        <div className="displayFlex gap4">
          {comparisonOperators.map((operator) => (
            <Button
              key={operator}
              size="sm"
              label={operator}
              variant={operator === comparisonType ? 'theme' : 'outline'}
              onClick={() => setComparisonType(operator)}
            />
          ))}
        </div>
        <TextField
          type="text"
          value={textFieldValue}
          placeholder={t('VANTAGE.EVIDENCE_TABLE.FILTERS.DURATION_PROMPT')}
          showError={!durationIsValid}
          extraClassNames={durationIsValid ? 'border-success' : ''}
          onChange={(e) => setTextFieldValue(e.target.value)}
        />

        <Button
          size="sm"
          label={t('CLEAR')}
          extraClassNames="btn-seeq"
          disabled={(model?.nanoSeconds ?? 0) === 0}
          variant="theme"
          onClick={() => {
            setTextFieldValue('');
            setDurationIsValid(true);
            onModelChange({ nanoSeconds: 0, operator: '', textFieldValue: '' });
          }}
        />
      </div>
      <div className="tw:flex tw:flex-row tw:text-center tw:justify-center tw:italic tw:pb-1 tw:text-xs tw:w-full">
        <span className={durationIsValid ? 'text-success' : 'text-danger'}>{feedbackMessage()}</span>
      </div>
    </div>
  );
};
